/**
 * Docx↔schema tag audit for the org template schema editor. Compares the
 * section-conditional tags found in the uploaded Word document (bodyTags,
 * extracted at upload with their '#'/'/'/'^' prefix intact) against the
 * '*_enabled' tags the schema's modules, order forms and schedules derive.
 *
 * Scope is deliberately narrow: only '*_enabled' tags are audited. Clause
 * tags ('clause_*'), plain placeholders and literal text braces are ignored —
 * they belong to other subsystems (clause library, field mappings, nullGetter
 * passthrough).
 */

import type { OrgTemplateSchema } from '../types/orgTemplate';
import {
  moduleEnabledTag,
  orderFormEnabledTag,
  scheduleEnabledTag,
} from './templateTags';

export interface UnbalancedTag {
  tag: string;
  /** 'missing-close': opened ('#'/'^') but never closed; 'missing-open': closed but never opened. */
  problem: 'missing-close' | 'missing-open';
}

export interface SchemaTagAuditResult {
  /** Tags the schema derives that the document does not contain at all. */
  missing: string[];
  /** '_enabled' tags in the document that no schema entity derives. */
  unknown: string[];
  /** '_enabled' tags whose open/close pairing is broken in the document. */
  unbalanced: UnbalancedTag[];
}

const isEnabledTag = (name: string): boolean =>
  name.endsWith('_enabled') && !name.startsWith('clause_');

export function auditSchemaTags(
  bodyTags: string[],
  schema: OrgTemplateSchema
): SchemaTagAuditResult {
  const expected: string[] = [];
  schema.modules.forEach((mod) => {
    expected.push(moduleEnabledTag(mod.id));
    mod.orderForms.forEach((orderForm) => {
      expected.push(orderFormEnabledTag(mod.id, orderForm.id));
    });
  });
  schema.schedules.forEach((schedule) => {
    expected.push(scheduleEnabledTag(schedule.id));
  });
  const expectedSet = new Set(expected);

  // Partition the document's '_enabled' tags by prefix. bodyTags is a
  // dedup'd set from the analyser, so balance is presence-based: an open
  // with no close (or vice versa) is broken regardless of how many times
  // Word repeats it.
  const opened = new Set<string>();
  const closed = new Set<string>();
  for (const raw of bodyTags) {
    const prefix = raw[0];
    const name = prefix === '#' || prefix === '/' || prefix === '^' ? raw.slice(1) : raw;
    if (!isEnabledTag(name)) continue;
    if (prefix === '#' || prefix === '^') opened.add(name);
    else if (prefix === '/') closed.add(name);
    // A bare '*_enabled' placeholder (no prefix) is not a section tag; ignore.
  }

  const present = new Set([...opened, ...closed]);

  const missing = expected.filter((tag) => !present.has(tag));

  const documentTags = [...present].sort();
  const unknown = documentTags.filter((tag) => !expectedSet.has(tag));

  const unbalanced: UnbalancedTag[] = [];
  for (const tag of documentTags) {
    if (opened.has(tag) && !closed.has(tag)) {
      unbalanced.push({ tag, problem: 'missing-close' });
    } else if (closed.has(tag) && !opened.has(tag)) {
      unbalanced.push({ tag, problem: 'missing-open' });
    }
  }

  return { missing, unknown, unbalanced };
}
